import { useEffect, useRef, useState } from 'react';

interface UseCopyToClipboardOptions {
  /** How long `copied` stays true after a copy, so the icon can switch to a check mark. */
  resetAfterMs?: number;
}

interface UseCopyToClipboardReturn {
  copied: boolean;
  /**
   * Copies `text` as plain text only. Deliberately no `text/html` flavour: rich editors prefer HTML on paste and
   * many of them collapse paragraph breaks, while plain text with `\n` keeps the line breaks everywhere.
   */
  copy: (text: string) => Promise<void>;
}

export const useCopyToClipboard = ({ resetAfterMs = 1500 }: UseCopyToClipboardOptions = {}): UseCopyToClipboardReturn => {
  const [copied, setCopied] = useState(false);
  const resetTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimeout.current), []);

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);

    setCopied(true);
    clearTimeout(resetTimeout.current);
    resetTimeout.current = setTimeout(() => setCopied(false), resetAfterMs);
  };

  return { copied, copy };
};
